import React, { useEffect, useState } from 'react';
import { FlightDetails, FlightStatus } from '../interfaces/flight.details';
import { RiArrowDropRightLine } from "react-icons/ri";
import toast from 'react-hot-toast';
import { BlinkBlur } from 'react-loading-indicators';
import FlightDetailPopup from './FlightDetailPopup';
import { useAirport } from '../context/AirportContext';
import { useFids } from '../hooks/useFids';
import { RemarkToCodeMapping, TripType } from '@industronics/fids-utils';
import moment from 'moment';
import TimeBlock from './TimeBlock';
import SafeFlightLogo from './SafeFlightLogo';
const gmtOffsetInt = Math.round(-new Date().getTimezoneOffset() / 60); // 8 for GMT+8

interface ArrivalProps {
    searchText: string;
}

const Arrivals: React.FC<ArrivalProps> = ({ searchText }) => {
    const { airportId } = useAirport();
    const { trips, loading, error } = useFids(airportId ?? '', TripType.Arrival);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [selectedFlight, setSelectedFlight] = useState<FlightDetails | null>(null); // Modal state
    const [stationIndices, setStationIndices] = useState<{ [key: string]: number }>({});


    const checkLastStation = (flight: FlightDetails) => {
        if (
            (stationIndices[flight._id] % (flight.stations.length - 1))
            === 0) return true;
        return false;
    };

    const setStations = async () => {
        try {
            const initialIndices: { [key: string]: number } = {};
            trips.forEach((flight: FlightDetails) => {
                initialIndices[flight._id] = 0;
            });
            setStationIndices(initialIndices);
        } catch (error) {
            if (error instanceof Error) {
                toast.error(error.message || "Failed to fetch flight data.");
            } else {
                toast.error("Failed to fetch flight data.");
            }
        } finally {
        }
    };

    useEffect(() => {
        setStations()
    }, [trips]);

    // Update stationIndices for flight stations
    useEffect(() => {
        const stationIntervalId = setInterval(() => {
            setStationIndices((prevIndices) => {
                const newIndices = { ...prevIndices };
                trips.forEach((flight) => {
                    if (flight.stations && flight.stations.length > 1) {
                        const currentIndex = prevIndices[flight._id] || 0;
                        newIndices[flight._id] = (currentIndex + 1) % (flight.stations.length - 1); // Exclude the final destination
                    }
                });
                return newIndices;
            });
        }, 3000);

        return () => {
            clearInterval(stationIntervalId);
        };
    }, [trips]);

    const status = (remarkCode: FlightStatus) => {
        switch (remarkCode?.key) {
            case RemarkToCodeMapping.DELAYED:
                return <p className="text-yellow-500 font-bold">{remarkCode.description}</p>;
            case RemarkToCodeMapping.LANDED:
                return <p className="text-green-500 font-bold">{remarkCode.description}</p>;
            case RemarkToCodeMapping.CANCELLED:
                return <p className="text-red-500 font-bold">{remarkCode.description}</p>;
            default:
                return remarkCode?.description;
        }
    };

    const statusMobileView = (remark: FlightStatus) => {
        switch (remark?.key) {
            case RemarkToCodeMapping.DELAYED:
                return <span className="bg-red-100 text-red-800 text-xs me-2 px-2.5 py-0.5 rounded border border-red-400 w-20 h-5 flex items-center justify-center">{remark.description}</span>;
            case RemarkToCodeMapping.CANCELLED:
                return <span className="bg-red-100 text-red-800 text-xs me-2 px-2.5 py-0.5 rounded border border-red-400 w-20 h-5 flex items-center justify-center">{remark.description}</span>;
            case RemarkToCodeMapping.LANDED:
                return <span className="bg-green-100 text-green-800 text-xs me-2 px-2.5 py-0.5 rounded border border-green-400 w-20 h-5 flex items-center justify-center">{remark.description}</span>;
            default:
                return remark?.description;
        }
    };

    const handleGateChange = (flight: FlightDetails) => {
        if (!flight.loungeCode) return;
        const firstChar = flight.loungeCode.match(/[A-Za-z]/)?.[0] || "";
        const numericPart = flight.loungeCode.match(/\d+/)?.[0].replace(/^0+/, "") || "";
        return firstChar + numericPart;
    };



    useEffect(() => {
        const normalize = (str: string) => String(str || '').toLowerCase().replace(/\s+/g, '');

        if (searchText) {
            const normalizedSearch = normalize(searchText);

            const filtered = trips.filter((flight) => {
                const matchesFlightCode = normalize(flight.flightNumber).includes(normalizedSearch);
                const matchesCodeShare = flight.codeShareFlight?.some((codeShare: string) =>
                    normalize(codeShare).includes(normalizedSearch)
                );
                const matchesAirline = normalize(flight?.airline?.name || '').includes(normalizedSearch);

                const matchesOrigin =
                    flight.stations.slice(0, -1).filter(x => normalize(String(x.city || '')).includes(normalizedSearch)).length > 0;

                return matchesFlightCode || matchesCodeShare || matchesAirline || matchesOrigin;
            });

            setFilteredData(filtered);
        } else {
            setFilteredData(trips);
        }
    }, [searchText, trips]);


    if (!airportId) {
        return <div className="w-full p-6 text-center">Please select an airport to view departures.</div>;
    }

    return (
        <div className="w-full">
            <div>
                <div className="hidden md:block w-full overflow-x-auto">
                    {!loading ? (
                        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 sticky uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3 rounded-tl-lg rounded-bl-lg">Flight No.</th>
                                    <th scope="col" className="px-6 py-3">Code Share</th>
                                    <th scope="col" className="px-6 py-3">Origin</th>
                                    <th scope="col" className="px-6 py-3">Scheduled Time</th>
                                    <th scope="col" className="px-6 py-3">Estimated Time</th>
                                    <th scope="col" className="px-6 py-3">Actual Time</th>
                                    <th scope="col" className="px-6 py-3">Terminal</th>
                                    <th scope="col" className="px-6 py-3">Gate</th>
                                    <th scope="col" className="px-6 py-3 text-center">Reclaim</th>
                                    <th scope="col" className="px-6 py-3 rounded-tr-lg rounded-br-lg">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.length > 0 ? (
                                    filteredData.map((flight: FlightDetails, index) => (
                                        <tr
                                            key={index}
                                            className="bg-inherit border-b transition-transform duration-200 text-gray-900 font-semibold ease-in-out hover:scale-105 hover:bg-slate-200 cursor-pointer"
                                            onClick={() => setSelectedFlight(flight)}
                                        >
                                            <th className="px-6 py-4 font-bold text-gray-900 whitespace-nowrap dark:text-white">
                                                <div className="flex items-center gap-2">
                                                    <SafeFlightLogo dimension='0180x0045' mainFlightNumber={flight.flightNumber} logoSize={{width:80, height: 40}}/>
                                                    <span>{flight.flightNumber}</span>
                                                </div>
                                            </th>
                                            <td className="px-6 py-4">
                                                {Array.isArray(flight.codeShareFlight) && flight.codeShareFlight.length > 0 ? (
                                                    <ul>
                                                        {flight.codeShareFlight.map((item: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined, i: React.Key | null | undefined) => (
                                                            <li key={i}>{item}</li>
                                                        ))}
                                                    </ul>
                                                ) : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-blue-800 text-lg">
                                                <div
                                                    style={{ textDecoration: flight.stations.length > 2 && checkLastStation(flight) ? "underline" : "initial" }}
                                                    className="px-6 py-4 text-blue-800 text-lg station-animation"
                                                    key={"station" + stationIndices[flight._id]}
                                                >
                                                    {(flight?.stations[
                                                        (stationIndices[flight._id] % (flight.stations.length - 1))
                                                    ]?.city)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4"><TimeBlock date={flight.scheduledTravelTime} /></td>
                                            <td className="px-6 py-4">
                                                {flight.ETA && flight.ETA !== flight.scheduledTravelTime && <TimeBlock date={flight.ETA} />}
                                            </td>
                                            <td className="px-6 py-4">
                                                {flight.actualArrivalDepartureTime && flight.actualArrivalDepartureTime !== flight.scheduledTravelTime && <TimeBlock date={flight.actualArrivalDepartureTime} />}
                                            </td>
                                            <td className="px-6 py-4">{flight.terminal?.code}</td>
                                            <td className="px-6 py-4">{handleGateChange(flight)}</td>
                                            <td className="px-6 py-4 text-center">{flight.loungReclaimCode ? (flight.loungReclaimCode?.replace(/0+/g, '')) : '-'}</td>
                                            <td className="px-6 py-4">{status(flight.remarkCode)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={10} className="h-96 text-center text-lg font-semibold text-gray-500">
                                            No Record Found!
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <div className="flex justify-center items-center h-96">
                            <BlinkBlur color="#437cc6" size="large" text="Getting Flights Ready..." textColor="" />
                        </div>
                    )}

                </div>

                {/* Card View for Mobile Screens */}
                <div className="block md:hidden bg-none p-2">
                    {!loading ? (
                        <div className={`${filteredData.length > 0 ? 'shadow-xl' : ''}`}>
                            {filteredData.length > 0 ? (
                                filteredData.map((flight: FlightDetails, index) => (
                                    <div
                                        key={index}
                                        className="bg-white border-b border-gray-300 p-4 flex gap-x-4"
                                        onClick={() => setSelectedFlight(flight)}
                                    >
                                        <div className="flex-none pt-1">
                                            <div className='w-14'><SafeFlightLogo dimension='0180x0045' mainFlightNumber={flight.flightNumber} logoSize={{width:80, height: 40}}/></div>
                                            <div className={`text-md ${flight.ETA && flight.ETA !== flight.scheduledTravelTime ? 'line-through text-blue-800' : 'font-semibold text-blue-600'}`}>
                                                {<div>
                                                    {moment(flight.scheduledTravelTime).utcOffset(gmtOffsetInt).format("HH:mm")}
                                                    <h5 className='text-blue-800' style={{ display: "block" }}>
                                                        {
                                                            moment(flight.scheduledTravelTime).utcOffset(gmtOffsetInt).isSame(moment(), 'day')
                                                                ? ''
                                                                : (
                                                                    <span
                                                                    >{moment(flight.scheduledTravelTime).utcOffset(gmtOffsetInt).format("DD MMM")}</span>
                                                                )}
                                                    </h5>
                                                </div>}
                                            </div>
                                            <div className='font-semibold text-md text-blue-600'>
                                                {flight.ETA && flight.ETA !== flight.scheduledTravelTime && <div>
                                                    {moment(flight.ETA).utcOffset(gmtOffsetInt).format("HH:mm")}
                                                    <h5 className='text-blue-800' style={{ display: "block" }}>
                                                        {
                                                            moment(flight.ETA).utcOffset(gmtOffsetInt).isSame(moment(), 'day')
                                                                ? ''
                                                                : (
                                                                    <span
                                                                    >{moment(flight.ETA).utcOffset(gmtOffsetInt).format("DD MMM")}</span>
                                                                )}
                                                    </h5>
                                                </div>}
                                            </div>
                                        </div>
                                        <div className="flex grow justify-between">
                                            <div className="text-left">
                                                {/* <p className='font-bold text-lg text-blue-900'>
                                                    {
                                                        (
                                                            flight?.stations?.slice(0, -1).map((station: { translations: any[]; }) => station.translations.filter(x => x.lang === "en")?.map(t => t.value)
                                                            ).flat()
                                                        )?.toLowerCase()
                                                            .split(' ')
                                                            .map((word: string) => word.charAt(0)?.toUpperCase() + word.slice(1))
                                                            .join(' ')}
                                                </p> */}

                                                <div
                                                    style={{ textDecoration: flight.stations.length > 2 && checkLastStation(flight) ? "underline" : "initial" }}
                                                    className="font-bold text-lg text-blue-900 px-6 py-4 text-blue-800 text-lg station-animation"
                                                    key={"station" + stationIndices[flight._id]}
                                                >
                                                    {(flight?.stations[
                                                        (stationIndices[flight._id] % (flight.stations.length - 1))
                                                    ]?.translations?.find(x => x?.lang === "en")?.value)}
                                                </div>

                                                <p className='font-normal text-md text-blue-800'>{flight.flightNumber}</p>

                                                {Array.isArray(flight.codeShareFlight) && flight.codeShareFlight.length > 0 ? (
                                                    <ul>
                                                        {flight.codeShareFlight.map((item: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined, i: React.Key | null | undefined) => (
                                                            <li className='font-normal text-md text-blue-800' key={i}>{item}</li>
                                                        ))}
                                                    </ul>
                                                ) : ''}

                                            </div>
                                            <div className="text-right flex items-center font-semibold">
                                                {statusMobileView(flight.remarkCode)} <RiArrowDropRightLine className='font-bold text-blue-800' size={30} />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (<div className="flex justify-center items-center h-96 bg-transparent font-semibold">No Record Found</div>)}
                        </div>
                    ) : (
                        <div className="flex justify-center items-center h-96">
                            <BlinkBlur color="#437cc6" size="small" text="Getting Flights Ready..." textColor="" />
                        </div>
                    )}
                </div>
            </div >

            {/* Modal */}
            {
                selectedFlight && (
                    <FlightDetailPopup
                        flight={selectedFlight}
                        onClose={() => setSelectedFlight(null)} // Close modal handler
                        modalTitle="Arrival"
                    />
                )
            }
        </div >
    );
};

export default Arrivals;
